import {Component, OnInit, ViewEncapsulation} from "@angular/core";
import {CommonModule} from "@angular/common";
import {NgSelectModule} from "@ng-select/ng-select";
import {TemplateService} from "../../../_services/template.service";
import {Fitrite365Template} from "../../../_models/Fitrite365Template";
import {FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {LabeledInputComponent} from "../../../shared/labeled-input/labeled-input.component";
import {SharedModule} from "../../../shared/shared.module";
import {SpecificationQuestionsRequestIds} from "../../../_models/SpecificationQuestionsRequestIds";
import {SpecificationQuestionsService} from "../../../_services/specification-questions.service";
import {SpecificationAnswer} from "../../../_models/SpecificationAnswer";
import {BaseComponent} from "../../../shared/base-component";
import {ToastrService} from "ngx-toastr";
import {QuoteCreationRequest} from "../../../_models/QuoteCreationRequest";
import {QuoteService} from "../../../_services/quote.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Customer} from "../../../_models/Customer";
import {CustomerService} from "../../../_services/customer.service";
import {catchError, map} from "rxjs/operators";
import {of} from "rxjs";
import {SweetAlert2Module} from "@sweetalert2/ngx-sweetalert2";
import {PlanTemplate} from "../../../_models/PlanTemplate";
import {Canvas, Rect} from "fabric";

@Component({
  standalone: true,
  templateUrl: './create-quote.component.html',
  imports: [
    CommonModule,
    NgSelectModule,
    CommonModule,
    CommonModule,
    CommonModule,
    ReactiveFormsModule,
    LabeledInputComponent,
    SharedModule,
    NgSelectModule,
    CommonModule,
    FormsModule,
    FormsModule,
    NgSelectModule,
    ReactiveFormsModule,
    CommonModule,
    CommonModule,
    CommonModule,
    SweetAlert2Module
  ],
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['create-quote.component.css']
})

export class CreateQuoteComponent extends BaseComponent implements OnInit {
  constructor(private templateService: TemplateService, private formBuilder: FormBuilder, private questionService: SpecificationQuestionsService,
              toasterService: ToastrService, private quoteService: QuoteService, private route: ActivatedRoute,
              private customerService: CustomerService, private router: Router) {
    super(toasterService)
  }

  public template: PlanTemplate;
  public colourSearchCriteria: SpecificationQuestionsRequestIds = new SpecificationQuestionsRequestIds();
  public balustradeColours: SpecificationAnswer[];
  public deckColours: SpecificationAnswer[];
  public colourOptions: SpecificationAnswer[];
  ids: number[] = [];
  templateId = '';
  public parks: Customer[] = [];
  customerIdSubmitted = false;
  deckColourIdSubmitted = false;
  balustradeColourIdSubmitted = false;


  quoteForm = this.formBuilder.group({
    customerName: new FormControl(null, [Validators.required]),
    customerEmail: new FormControl(null, [Validators.required]),
    customerPhone: new FormControl(null, [Validators.required]),
    templateId: new FormControl(null),
    deckColour: new FormControl(null, [Validators.required]),
    balustradeColour: new FormControl(null, [Validators.required]),
    comments: new FormControl(null),
    plot: new FormControl(null, [Validators.required]),
    customerId: new FormControl(null, [Validators.required])
  })

  ngOnInit() {
    this.templateId = this.route.snapshot.paramMap.get('templateId');
    this.getTemplate();
    this.getColours();
    this.getParks();
  }

  getTemplate() {
    this.templateService.getPlanTemplateById(this.templateId)
      .subscribe(templates => {
        this.template = templates;
      })
  }

  get f() {
    return this.quoteForm.controls;
  }

  getColours() {
    this.colourSearchCriteria.ids = [3, 4];
    this.questionService.getSpecificationQuestions(this.colourSearchCriteria)
      .subscribe(questions => {
        this.balustradeColours = questions.find((x) => x.id == 3).specificationAnswers;
        this.deckColours = questions.find((x) => x.id == 4).specificationAnswers;
      })
  }

  createQuote() {
    this.customerIdSubmitted = true;
    this.deckColourIdSubmitted = true;
    this.balustradeColourIdSubmitted = true;


    if (this.isLoading || !this.quoteForm.valid) {
      this.quoteForm.markAllAsTouched();
      this.showAllFormValidation(this.quoteForm);
      return;
    }

    const quote = this.quoteForm.value;
    quote.templateId = this.template?.id;

    const request = Object.assign(new QuoteCreationRequest(), this.quoteForm.value);

    this.quoteService.createQuote(request)
      .pipe(
        map(q => {
          this.toasterService.success('Quote Created');
          let x = q.id;
          this.router.navigate(['/quotes/' + q.id]);
        }),
        catchError(e => {
          this.toasterService.error(e);
          return of();
        }))
      .subscribe()
  }

  getParks() {
    //TODO this needs actually doing, rather than a placeholder
    this.customerService.getCustomersByUser()
      .subscribe(customers => {
        this.parks = customers;
      })
  }

  createTemplateImage(data: string): string {
    if (!data) return '';

    const canvasEl = document.createElement('canvas');
    canvasEl.width = 200;
    canvasEl.height = 200;

    const canvas = new Canvas(canvasEl, {
      backgroundColor: '#f3f3f3',
      renderOnAddRemove: false
    });

    let rects: any[];

    try {
      rects = JSON.parse(data).map((r: any) => ({
        ...r,
        x: Number(r.x),
        y: Number(r.y),
        width: Number(r.width),
        length: Number(r.length)
      }));
    } catch {
      console.error('Invalid planData JSON', data);
      return '';
    }

    // 🔢 Correct draw order
    rects.sort(
      (a, b) => this.getLayerPriority(a.type) - this.getLayerPriority(b.type)
    );

    // 📐 Bounds
    const minX = Math.min(...rects.map(r => r.x));
    const minY = Math.min(...rects.map(r => r.y));
    const maxX = Math.max(...rects.map(r => r.x + r.width));
    const maxY = Math.max(...rects.map(r => r.y + r.length));

    const layoutWidth = maxX - minX;
    const layoutHeight = maxY - minY;

    // 🔑 SVG-style uniform scale
    const scale = Math.min(
      canvas.width! / layoutWidth,
      canvas.height! / layoutHeight
    );

    // 🔑 Anchor to top-left
    const offsetX = -minX * scale;
    const offsetY = -minY * scale;

    rects.forEach(r => {
      // 🧠 SNAP EDGES TO INTEGER PIXELS
      const left   = Math.round(r.x * scale + offsetX);
      const top    = Math.round(r.y * scale + offsetY);
      const right  = Math.round((r.x + r.width) * scale + offsetX);
      const bottom = Math.round((r.y + r.length) * scale + offsetY);
      const width  = right - left;
      const height = bottom - top;

      canvas.add(new Rect({
        left,
        top,
        width,
        height,
        fill: this.getColour(r.type) || '#cccccc',

        // 🔥 FIXES GAPS AND ARTIFACTS
        stroke: null,
        strokeWidth: 0,
        strokeUniform: true,
        noScaleCache: false,
        selectable: false,
        objectCaching: false
      }));
    });

    canvas.renderAll();

    const dataUrl = canvas.toDataURL({
      format: 'png',
      multiplier: 1
    });

    canvas.dispose();
    return dataUrl;
  }

  getLayerPriority(type: string | null | undefined): number {
    if (!type) return 0;

    const t = type.toLowerCase();

    if (t.includes('skirt')) return 0;
    if (t.includes('deck')) return 1;
    if (t.includes('walk') || t.includes('ramp')) return 2;
    if (t.includes('step')) return 3;
    if (t.includes('gate')) return 4;

    return 1;
  }

  getColour(type: string | null | undefined): string {
    if (!type) return '#cccccc';

    const t = type.toLowerCase();

    if (t.includes('deck')) return '#6c83ff';     // blue
    if (t.includes('skirt')) return '#66e26f';    // green
    if (t.includes('step')) return '#ffb74d';     // orange
    if (t.includes('gate')) return '#000000';     // black

    return '#cccccc';
  }
}
